import { useAddOrUpdateCommunityReview } from '@/actions/community-review.actions.js';
import useStore from '@/stores/store';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import Avatar from '@ui/Avatar';
import { cx } from 'cva';
import { isEmpty, trim } from 'lodash-es';
import Image from 'next/image';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { MdClose, MdPhoto, MdSentimentSatisfiedAlt } from 'react-icons/md';
import { animated, useSpring } from 'react-spring';
import { useFilePicker } from 'use-file-picker';
import useEmojiDialogStore from '../../EmojiPicker/emojiDialog.store';
import Button from '../../next-ui/Button';
import Typography from '../../next-ui/Typography';
import TagMentionInput from '../TagMentionInput/TagMentionInput';

function CommunityReviewForm(props) {
  const {
    animation = { blink: false },
    nonProfitId,
    parentReviewId = null,
    helperText = '',
    mode = 'create',
    review = null,
    onComplete = () => {},
  } = props;

  const userProfile = useStore((state) => state.user.profile);
  const isReply = parentReviewId !== null;
  const [value, setValue] = useState(review?.message || '');
  const requestEmoji = useEmojiDialogStore((state) => state.requestEmoji);

  const inputRef = useRef(null);

  // REVIEW MUTATIONS
  const {
    mutate: addOrUpdateReview,
    isLoading: isSaving,
    error,
  } = useAddOrUpdateCommunityReview();

  // used to blink the review form when the user clicks on the reply button
  const [isBlinking, setIsBlinking] = useState(false);
  const blinkProps = useSpring({
    from: { opacity: 1 },
    to: [{ opacity: isBlinking ? 0 : 1 }, { opacity: 1 }],
    config: { duration: 200 },
    onRest: () => {
      if (isBlinking) {
        setIsBlinking(false);
        inputRef.current.focus();
      }
    },
  });
  useEffect(() => {
    if (animation.blink) {
      setIsBlinking(true);
    }
  }, [animation]);

  // FILE PICKER
  const [selectedImage, setSelectedImage] = useState({
    content: review?.attachmentUrl
      ? getQualifiedImageUrl(review.attachmentUrl)
      : null,
    file: null,
  });
  const [openFileSelector, { filesContent, plainFiles, errors: fileErrors }] =
    useFilePicker({
      readAs: 'DataURL',
      accept: 'image/*',
      multiple: false,
      maxFileSize: 2, // in megabytes
    });
  useEffect(() => {
    if (filesContent && filesContent[0]) {
      setSelectedImage({
        content: filesContent[0].content,
        file: plainFiles[0],
      });
    }
  }, [filesContent, plainFiles]);

  // drived states
  const hasAttachment = !!selectedImage.content;
  const canPost = !isEmpty(trim(value)) || hasAttachment;

  // callback handlers
  const handleSubmit = async () => {
    if (canPost && !isSaving) {
      // if (isReply) {
      await addOrUpdateReview({
        nonProfitId,
        parentReviewId,
        message: value,
        attachment: selectedImage,
        reviewId: review?.id,
      });
      // } else {
      //   await addOrUpdateReview({
      //     nonProfitId,
      //     message: value,
      //     attachment: selectedImage,
      //     reviewId: review?.id,
      //   });
      // }
      setValue('');
      handleClearSelectedImage();
      onComplete();
    }
  };

  const handleEmojiPick = async () => {
    const emoji = await requestEmoji();
    if (emoji) {
      const cursor = inputRef.current.selectionStart;
      const text = value.slice(0, cursor) + emoji + value.slice(cursor);
      setValue(text);
    }
  };

  const handleShowFileSelector = () => {
    openFileSelector();
  };

  const handleClearSelectedImage = () => {
    setSelectedImage({
      content: null,
      file: null,
    });
  };

  return (
    <animated.div style={blinkProps}>
      <div className="flex items-start justify-start gap-1">
        {/* <ProfileAvatar
          profile={userProfile.selectedProfile}
          className={isReply ? 'medium' : 'large'}
        /> */}
        {mode === 'create' && (
          <Avatar
            src={userProfile.avatar}
            alt={userProfile.fullName}
            size="sm"
          />
        )}
        <div className="flex w-full flex-1">
          <div className="flex w-full flex-col items-start justify-start gap-1 rounded-lg border bg-primary-light-1 pr-2">
            <TagMentionInput
              value={value}
              onChange={(value) => setValue(value)}
              ref={inputRef}
              placeholder="Write a comment..."
              error={error?.message}
            />
            {hasAttachment && (
              <div className="mt-4 grid w-full grid-cols-1 pl-2 sm:grid-cols-2 md:grid-cols-3">
                <div className="relative h-32 w-full">
                  <Image
                    src={selectedImage.content || ''}
                    fill
                    sizes="(max-width: 640px) 100vw, 640px"
                    alt="User uploaded image"
                    className="rounded object-cover"
                  />
                  <div className="absolute right-0 top-0">
                    <Button
                      variant="icon"
                      size="xs"
                      className="!bg-secondary"
                      onClick={handleClearSelectedImage}>
                      <MdClose className="h-6 w-6 text-white" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex w-full items-center justify-start gap-2">
              <Button variant="icon" size="md" onClick={handleEmojiPick}>
                <MdSentimentSatisfiedAlt className="h-6 w-6 text-text-secondary" />
              </Button>
              {/* <Button
                variant="icon"
                size="md"
                onClick={handleShowFileSelector}
                type="button">
                <MdPhoto className="h-6 w-6 text-text-secondary" />
              </Button> */}
              <div className="ml-auto flex items-center gap-2">
                {mode === 'edit' && (
                  <Button
                    type="button"
                    variant="outlined"
                    size="sm"
                    onClick={() => onComplete()}>
                    Cancel
                  </Button>
                )}
                <Button
                  variant="filled"
                  type="button"
                  size="sm"
                  onClick={handleSubmit}
                  className={cx(
                    'transform transition duration-300 ease-in-out hover:scale-100 hover:opacity-100',
                    canPost
                      ? 'visible scale-100 opacity-100'
                      : 'invisible scale-95 opacity-0',
                  )}>
                  {mode === 'edit' ? 'Save' : 'Post'}
                </Button>
              </div>
            </div>
          </div>

          {/* {selectedImage && (
            <Box position="relative" maxWidth={200} margin="0 0 16px 16px">
              <StyledRoundIconButton
                onClick={deleteAttachmentFile}
                style={{ position: 'absolute', top: 4, right: 0 }}>
                <IoCloseSharp />
              </StyledRoundIconButton>
              <StyledPostPhoto
                src={
                  isString(selectedImage)
                    ? getQualifiedImageUrl(selectedImage)
                    : selectedImage.preview
                }
                alt="User uploaded image"
              />
            </Box>
          )} */}
          {/* </FormContainer> */}

          <Typography variant="caption" color="textSecondary">
            {helperText}
          </Typography>

          {/* {showSubmitButton && (
            <Stack direction="row" alignItems="center" gap={2}>
              <Button
                variant="filled"
                size="sm"
                onClick={handleSubmit}
                loading={loading}>
                Post
              </Button>

              {mode === 'edit' && (
                <Button size="sm" variant="outlined" onClick={handleCancel}>
                  Cancel
                </Button>
              )}
            </Stack>
          )} */}
        </div>
      </div>
    </animated.div>
  );
}

export default CommunityReviewForm;
